import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import FancyPaper from "../components/FancyPaper";
import { useApp } from "../hooks/useApp";
import {
  DefaultProjectVehicle,
  getDefaultProjectVehicle,
  saveDefaultProjectVehicle,
} from "../services/projects/DefaultProjectVehicleService";

// En el texto del remitente, este marcador se reemplaza por el nombre del
// proyecto cuando se siembran sus datos (PROJECTS_API usa el mismo). Si el texto
// no lo trae, no se escribe el nombre del proyecto.
const PROJECT_NAME_TOKEN = "{proyecto}";
const PREVIEW_PROJECT_NAME = "Nombre del proyecto";

// El remitente se edita en tres partes alrededor del marcador del proyecto, que
// siempre se ve y no se puede borrar: texto arriba, texto al lado (antes y
// después, en la misma línea) y texto abajo.
interface SenderParts {
  above: string;
  prefix: string;
  suffix: string;
  below: string;
  includeProject: boolean;
}

const EMPTY_SENDER: SenderParts = {
  above: "",
  prefix: "",
  suffix: "",
  below: "",
  includeProject: true,
};

const buildSender = (parts: SenderParts): string => {
  const lines = [...parts.above.split("\n")];
  if (parts.includeProject) {
    lines.push(`${parts.prefix}${PROJECT_NAME_TOKEN}${parts.suffix}`);
  }
  lines.push(...parts.below.split("\n"));
  return lines.join("\n");
};

const parseSender = (text: string | null | undefined): SenderParts => {
  const lines = (text ?? "").split("\n");
  const index = lines.findIndex((line) => line.includes(PROJECT_NAME_TOKEN));
  if (index < 0) {
    // Sin marcador: el remitente no lleva el nombre del proyecto.
    return { ...EMPTY_SENDER, above: lines.join("\n"), includeProject: false };
  }
  const [prefix, ...rest] = lines[index].split(PROJECT_NAME_TOKEN);
  return {
    above: lines.slice(0, index).join("\n"),
    prefix,
    suffix: rest.join(PROJECT_NAME_TOKEN),
    below: lines.slice(index + 1).join("\n"),
    includeProject: true,
  };
};

// Igual que el backend: sin espacios sobrantes ni líneas vacías.
const normalizeLines = (text: string | null | undefined) =>
  (text ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "")
    .join("\n");

// Jackson puede entregar la fecha como texto ISO o como arreglo [y, m, d, h, mi, s].
const formatModifiedDate = (value: DefaultProjectVehicle["lastModifiedDate"]) => {
  if (!value) return "";
  const date = Array.isArray(value)
    ? dayjs(new Date(value[0], value[1] - 1, value[2], value[3] ?? 0, value[4] ?? 0, value[5] ?? 0))
    : dayjs(value);
  return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "";
};

const SectionTitle = ({ children }: { children: string }) => (
  <Box sx={{ mb: 2, pb: 1, borderBottom: "2px solid #d32f2f" }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#d32f2f" }}>
      {children}
    </Typography>
  </Box>
);

const ManagementDefaultProjectVehicles = () => {
  const { setLoading, setErrorMsg, setSuccessMsg, authInfo } = useApp();
  const [saved, setSaved] = useState<DefaultProjectVehicle | null>(null);
  const [content, setContent] = useState("");
  const [sender, setSender] = useState<SenderParts>(EMPTY_SENDER);
  const [signatureContent, setSignatureContent] = useState("");

  const applyLoaded = (data: DefaultProjectVehicle) => {
    setSaved(data);
    setContent(data.content ?? "");
    setSender(parseSender(data.senderContent));
    setSignatureContent(data.signatureContent ?? "");
  };

  const load = useCallback(async () => {
    try {
      setLoading && setLoading(true);
      applyLoaded(await getDefaultProjectVehicle());
    } catch (error: any) {
      setErrorMsg &&
        setErrorMsg(error?.message ?? "No se pudieron cargar los datos por defecto");
    } finally {
      setLoading && setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const senderContent = buildSender(sender);
  const dirty =
    saved !== null &&
    (content !== (saved.content ?? "") ||
      normalizeLines(senderContent) !== normalizeLines(saved.senderContent) ||
      signatureContent !== (saved.signatureContent ?? ""));
  const vehicleCount = content
    .split("\n")
    .filter((line) => line.trim() !== "").length;
  const updateSender = (changes: Partial<SenderParts>) =>
    setSender((current) => ({ ...current, ...changes }));

  const handleSave = async () => {
    try {
      setLoading && setLoading(true);
      applyLoaded(
        await saveDefaultProjectVehicle(
          content,
          senderContent,
          signatureContent,
          authInfo?.username ?? ""
        )
      );
      setSuccessMsg &&
        setSuccessMsg(
          "Datos por defecto guardados. Aplican a los proyectos que se creen o inicialicen desde ahora."
        );
    } catch (error: any) {
      setErrorMsg &&
        setErrorMsg(
          error?.response?.data?.message ??
            error?.message ??
            "No se pudieron guardar los datos por defecto"
        );
    } finally {
      setLoading && setLoading(false);
    }
  };

  const handleDiscard = () => {
    if (saved) applyLoaded(saved);
  };

  const modifiedBy = saved?.lastModifiedBy;
  const modifiedDate = formatModifiedDate(saved?.lastModifiedDate);

  return (
    <FancyPaper pagetitle="Datos por defecto de lista de personal">
      <Grid container spacing={4} sx={{ pb: 8 }}>
        <Grid item xs={12} md={9} lg={7}>
          <Typography variant="body2" sx={{ mb: 4 }}>
            Estos textos se copian a la lista de personal de cada proyecto cuando
            se crea el proyecto, o la primera vez que se entra a la lista de
            personal de un proyecto que todavía no tiene sus datos por defecto.
            Los proyectos que ya los recibieron no cambian: ahí se editan desde
            su propia lista de personal.
          </Typography>

          <SectionTitle>Registro de remitente</SectionTitle>
          <Typography variant="body2" sx={{ mb: 2 }}>
            El marcador <strong>proyecto</strong> siempre está: indica dónde se
            escribe el nombre de cada proyecto. Lo que pongas arriba, al lado y
            abajo es igual para todos.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Arriba del proyecto"
            value={sender.above}
            onChange={(event) => updateSender({ above: event.target.value })}
            sx={{ mb: 2 }}
          />
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 2,
              mb: 1,
            }}
          >
            <TextField
              size="small"
              label="Al lado, antes"
              value={sender.prefix}
              disabled={!sender.includeProject}
              onChange={(event) =>
                updateSender({ prefix: event.target.value.replace(/\r?\n/g, " ") })
              }
              sx={{ flex: "1 1 160px" }}
            />
            <Chip
              data-testid="project-marker"
              label="proyecto"
              color={sender.includeProject ? "primary" : "default"}
              variant={sender.includeProject ? "filled" : "outlined"}
              sx={{
                fontWeight: 700,
                textDecoration: sender.includeProject ? "none" : "line-through",
              }}
            />
            <TextField
              size="small"
              label="Al lado, después"
              value={sender.suffix}
              disabled={!sender.includeProject}
              onChange={(event) =>
                updateSender({ suffix: event.target.value.replace(/\r?\n/g, " ") })
              }
              sx={{ flex: "1 1 160px" }}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={sender.includeProject}
                onChange={(event) =>
                  updateSender({ includeProject: event.target.checked })
                }
              />
            }
            label="Escribir el nombre del proyecto en el remitente"
            sx={{ mb: 1 }}
          />
          {!sender.includeProject && (
            <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
              No se escribirá el nombre del proyecto en el remitente de los
              proyectos nuevos.
            </Typography>
          )}
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Abajo del proyecto"
            value={sender.below}
            onChange={(event) => updateSender({ below: event.target.value })}
            sx={{ mb: 2 }}
          />
          <Typography variant="caption" color="text.secondary">
            Vista previa
          </Typography>
          <Box
            data-testid="sender-preview"
            sx={{
              mt: 0.5,
              mb: 4,
              p: 2,
              border: "1px dashed #bdbdbd",
              borderRadius: "8px",
              whiteSpace: "pre-line",
              minHeight: 48,
            }}
          >
            {normalizeLines(
              senderContent.split(PROJECT_NAME_TOKEN).join(PREVIEW_PROJECT_NAME)
            )}
          </Box>

          <SectionTitle>Lista de vehículos registrados</SectionTitle>
          <TextField
            fullWidth
            multiline
            minRows={12}
            label="Vehículos (uno por línea)"
            value={content}
            onChange={(event) => setContent(event.target.value)}
            helperText={`${vehicleCount} ${
              vehicleCount === 1 ? "vehículo" : "vehículos"
            }. Las líneas vacías se omiten al guardar.`}
            sx={{ mb: 4 }}
          />

          <SectionTitle>Lista de firmas registradas</SectionTitle>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Firmas (un renglón por línea)"
            value={signatureContent}
            onChange={(event) => setSignatureContent(event.target.value)}
            helperText="Las líneas vacías se omiten al guardar."
          />

          <Box
            sx={{
              mt: 3,
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {modifiedBy
                ? `Última modificación: ${modifiedBy}${
                    modifiedDate ? ` · ${modifiedDate}` : ""
                  }`
                : ""}
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                disabled={!dirty}
                onClick={handleDiscard}
                sx={{ borderRadius: "8px!important" }}
              >
                Descartar cambios
              </Button>
              <Button
                variant="contained"
                disabled={!dirty}
                onClick={handleSave}
                sx={{ borderRadius: "8px!important" }}
              >
                Guardar
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </FancyPaper>
  );
};

export default ManagementDefaultProjectVehicles;
